import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  FileText, Download, Send, Search, CheckCircle, XCircle, 
  AlertTriangle, Building, CreditCard, Calculator, FileCheck,
  Users, Calendar, RefreshCw, Plus, Loader2
} from "lucide-react";
import axios from "axios";

const SwedishIntegrations = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("peppol");
  const [loading, setLoading] = useState(false);

  return (
    <div className="p-6 space-y-6" data-testid="swedish-integrations">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Svenska Integrationer</h1>
          <p className="text-muted-foreground">E-faktura, Bankgiro, AGI, ROT/RUT och F-skatt</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="peppol" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            E-faktura
          </TabsTrigger>
          <TabsTrigger value="bankgiro" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Bankgiro
          </TabsTrigger>
          <TabsTrigger value="agi" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            AGI
          </TabsTrigger>
          <TabsTrigger value="rotrut" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            ROT/RUT
          </TabsTrigger>
          <TabsTrigger value="fskatt" className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            F-skatt
          </TabsTrigger>
        </TabsList>

        <TabsContent value="peppol">
          <PeppolSection token={token} />
        </TabsContent>

        <TabsContent value="bankgiro">
          <BankgiroSection token={token} />
        </TabsContent>

        <TabsContent value="agi">
          <AGISection token={token} />
        </TabsContent>

        <TabsContent value="rotrut">
          <RotRutSection token={token} />
        </TabsContent>

        <TabsContent value="fskatt">
          <FSkattSection token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ PEPPOL E-INVOICE SECTION ============

const PeppolSection = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [generatedXml, setGeneratedXml] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/sales-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(res.data.filter(inv => inv.status === 'sent' || inv.status === 'paid'));
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const generatePeppol = async (invoiceId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/peppol/generate/${invoiceId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedXml(res.data);
      toast.success("Peppol XML genererad!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte generera Peppol XML");
    } finally {
      setLoading(false);
    }
  };

  const downloadXml = () => {
    if (!generatedXml) return;
    const blob = new Blob([generatedXml.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `peppol_${generatedXml.invoice_number}.xml`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Peppol E-faktura (BIS Billing 3.0)
          </CardTitle>
          <CardDescription>
            Generera och skicka e-fakturor via Peppol-nätverket. Obligatoriskt för B2G-fakturor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <Label>Välj faktura att konvertera</Label>
            <Select value={selectedInvoice || ""} onValueChange={setSelectedInvoice}>
              <SelectTrigger>
                <SelectValue placeholder="Välj faktura..." />
              </SelectTrigger>
              <SelectContent>
                {invoices.map(inv => (
                  <SelectItem key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {inv.customer_name} ({inv.total?.toLocaleString('sv-SE')} kr)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button 
              onClick={() => generatePeppol(selectedInvoice)}
              disabled={!selectedInvoice || loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Generera Peppol XML
            </Button>
          </div>

          {generatedXml && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Genererad XML för {generatedXml.invoice_number}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={downloadXml}>
                      <Download className="h-4 w-4 mr-2" />
                      Ladda ner
                    </Button>
                    <Button size="sm">
                      <Send className="h-4 w-4 mr-2" />
                      Skicka via Peppol
                    </Button>
                  </div>
                </div>
                <pre className="text-xs bg-background p-3 rounded-lg overflow-auto max-h-64 border">
                  {generatedXml.xml?.substring(0, 2000)}...
                </pre>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Peppol Access Point</CardTitle>
          <CardDescription>
            Konfigurering av Peppol-anslutning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 p-4 border rounded-lg bg-amber-50 dark:bg-amber-950/20">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <div>
              <p className="font-medium">Peppol Access Point krävs</p>
              <p className="text-sm text-muted-foreground">
                För att skicka e-fakturor via Peppol behöver du ansluta till en certifierad Access Point 
                som InExchange, Pagero eller liknande.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ BANKGIRO SECTION ============

const BankgiroSection = ({ token }) => {
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [generatedFile, setGeneratedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSupplierInvoices();
  }, []);

  const fetchSupplierInvoices = async () => {
    try {
      const res = await axios.get(`${API}/supplier-invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierInvoices(res.data.filter(inv => inv.status === 'unpaid' || inv.status === 'partially_paid'));
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const toggleInvoice = (id) => {
    setSelectedInvoices(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const generatePaymentFile = async () => {
    if (selectedInvoices.length === 0) {
      toast.error("Välj minst en faktura");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/bankgiro/supplier-payments/from-invoices?payment_date=${paymentDate}&${selectedInvoices.map(id => `invoice_ids=${id}`).join('&')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedFile(res.data);
      toast.success(`Betalfil genererad med ${res.data.payment_count} betalningar`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte generera betalfil");
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = () => {
    if (!generatedFile) return;
    const blob = new Blob([generatedFile.file_content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generatedFile.filename;
    a.click();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bankgiro Leverantörsbetalningar
          </CardTitle>
          <CardDescription>
            Generera betalfiler för massbetalning till leverantörer via Bankgiro
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label>Betalningsdatum</Label>
              <Input 
                type="date" 
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
            <Button onClick={generatePaymentFile} disabled={loading || selectedInvoices.length === 0}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Generera betalfil ({selectedInvoices.length})
            </Button>
          </div>

          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left w-12">
                    <input 
                      type="checkbox"
                      checked={selectedInvoices.length === supplierInvoices.length}
                      onChange={() => setSelectedInvoices(
                        selectedInvoices.length === supplierInvoices.length 
                          ? [] 
                          : supplierInvoices.map(i => i.id)
                      )}
                    />
                  </th>
                  <th className="p-3 text-left">Fakturanr</th>
                  <th className="p-3 text-left">Leverantör</th>
                  <th className="p-3 text-left">Förfallodatum</th>
                  <th className="p-3 text-right">Belopp</th>
                </tr>
              </thead>
              <tbody>
                {supplierInvoices.map(inv => (
                  <tr key={inv.id} className="border-t hover:bg-muted/30">
                    <td className="p-3">
                      <input 
                        type="checkbox"
                        checked={selectedInvoices.includes(inv.id)}
                        onChange={() => toggleInvoice(inv.id)}
                      />
                    </td>
                    <td className="p-3 font-mono">{inv.invoice_number}</td>
                    <td className="p-3">{inv.supplier_name}</td>
                    <td className="p-3">{inv.due_date}</td>
                    <td className="p-3 text-right font-mono">
                      {(inv.remaining_amount || inv.total_amount)?.toLocaleString('sv-SE')} kr
                    </td>
                  </tr>
                ))}
                {supplierInvoices.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      Inga obetalda leverantörsfakturor
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {generatedFile && (
            <Card className="bg-green-50 dark:bg-green-950/20 border-green-200">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-300">
                      Betalfil genererad
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {generatedFile.payment_count} betalningar, totalt {generatedFile.total_amount?.toLocaleString('sv-SE')} kr
                    </p>
                  </div>
                  <Button onClick={downloadFile}>
                    <Download className="h-4 w-4 mr-2" />
                    Ladda ner {generatedFile.filename}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ AGI SECTION ============

const AGISection = ({ token }) => {
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reports, setReports] = useState([]);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/agi/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data);
    } catch (error) {
      console.error('Error fetching AGI reports:', error);
    }
  };

  const generateAGI = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/agi/generate/${period}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedReport(res.data);
      fetchReports();
      toast.success("AGI-rapport genererad!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte generera AGI");
    } finally {
      setLoading(false);
    }
  };

  const downloadXml = () => {
    if (!generatedReport) return;
    const blob = new Blob([generatedReport.xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AGI_${period}.xml`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Arbetsgivardeklaration (AGI)
          </CardTitle>
          <CardDescription>
            Generera AGI för rapportering till Skatteverket
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="space-y-2">
              <Label>Period (ÅÅÅÅ-MM)</Label>
              <Input 
                type="month" 
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </div>
            <Button onClick={generateAGI} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
              Generera AGI
            </Button>
          </div>

          {generatedReport && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="font-medium">AGI för {generatedReport.period}</p>
                    <p className="text-sm text-muted-foreground">
                      {generatedReport.employee_count} anställda
                    </p>
                  </div>
                  <Button onClick={downloadXml}>
                    <Download className="h-4 w-4 mr-2" />
                    Ladda ner XML
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-sm text-muted-foreground">Bruttolön</p>
                    <p className="text-lg font-semibold">
                      {generatedReport.summary?.total_gross_salary?.toLocaleString('sv-SE')} kr
                    </p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-sm text-muted-foreground">Avdragen skatt</p>
                    <p className="text-lg font-semibold">
                      {generatedReport.summary?.total_tax_deducted?.toLocaleString('sv-SE')} kr
                    </p>
                  </div>
                  <div className="p-3 bg-background rounded-lg">
                    <p className="text-sm text-muted-foreground">Arbetsgivaravgift</p>
                    <p className="text-lg font-semibold">
                      {generatedReport.summary?.total_employer_contributions?.toLocaleString('sv-SE')} kr
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tidigare rapporter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {reports.map(report => (
              <div key={report.id} className="flex justify-between items-center p-3 border rounded-lg">
                <div>
                  <p className="font-medium">{report.period}</p>
                  <p className="text-sm text-muted-foreground">
                    {report.employee_count} anställda, {report.total_gross?.toLocaleString('sv-SE')} kr brutto
                  </p>
                </div>
                <Badge variant={report.status === 'submitted' ? 'success' : 'secondary'}>
                  {report.status === 'submitted' ? 'Inskickad' : 'Genererad'}
                </Badge>
              </div>
            ))}
            {reports.length === 0 && (
              <p className="text-center text-muted-foreground py-4">Inga tidigare rapporter</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ ROT/RUT SECTION ============

const RotRutSection = ({ token }) => {
  const [deductionType, setDeductionType] = useState("rot");
  const [laborCost, setLaborCost] = useState("");
  const [materialCost, setMaterialCost] = useState("");
  const [propertyDesignation, setPropertyDesignation] = useState("");
  const [buyers, setBuyers] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearSummary, setYearSummary] = useState(null);

  useEffect(() => {
    fetchYearSummary();
  }, []);

  const fetchYearSummary = async () => {
    try {
      const year = new Date().getFullYear();
      const res = await axios.get(`${API}/rot-rut/summary/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYearSummary(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const calculate = async () => {
    const buyerList = buyers.split(',').map(b => b.trim()).filter(Boolean);
    if (!laborCost || buyerList.length === 0) {
      toast.error("Fyll i arbetskostnad och minst ett personnummer");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/rot-rut/calculate`, {
        deduction_type: deductionType,
        labor_cost: parseFloat(laborCost),
        material_cost: parseFloat(materialCost) || 0,
        property_designation: propertyDesignation,
        buyer_personal_numbers: buyerList
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte beräkna avdrag");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Beräkna ROT/RUT-avdrag
            </CardTitle>
            <CardDescription>
              Beräkna skattereduktion för ROT (renovering) eller RUT (hushållstjänster)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Typ av avdrag</Label>
              <Select value={deductionType} onValueChange={setDeductionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rot">ROT (Renovering, 30%)</SelectItem>
                  <SelectItem value="rut">RUT (Hushållstjänster, 50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Arbetskostnad (inkl. moms)</Label>
                <Input 
                  type="number"
                  value={laborCost}
                  onChange={(e) => setLaborCost(e.target.value)}
                  placeholder="50000"
                />
              </div>
              <div className="space-y-2">
                <Label>Materialkostnad</Label>
                <Input 
                  type="number"
                  value={materialCost}
                  onChange={(e) => setMaterialCost(e.target.value)}
                  placeholder="10000"
                />
              </div>
            </div>

            {deductionType === "rot" && (
              <div className="space-y-2">
                <Label>Fastighetsbeteckning</Label>
                <Input 
                  value={propertyDesignation}
                  onChange={(e) => setPropertyDesignation(e.target.value)}
                  placeholder="Stockholm Södermalm 1:5"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Köparnas personnummer (kommaseparerat)</Label>
              <Textarea 
                value={buyers}
                onChange={(e) => setBuyers(e.target.value)}
                placeholder="198001011234, 198505052345"
              />
            </div>

            <Button onClick={calculate} disabled={loading} className="w-full">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              Beräkna avdrag
            </Button>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {result && (
            <Card className="border-2 border-primary">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Beräknat {result.deduction_type}-avdrag</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Arbetskostnad</p>
                    <p className="text-lg font-semibold">{result.labor_cost?.toLocaleString('sv-SE')} kr</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Materialkostnad</p>
                    <p className="text-lg font-semibold">{result.material_cost?.toLocaleString('sv-SE')} kr</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span>Avdragsprocent</span>
                    <span className="font-semibold">{result.deduction_percentage}%</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Antal köpare</span>
                    <span className="font-semibold">{result.num_buyers}</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Avdrag per person</span>
                    <span className="font-semibold">{result.deduction_per_person?.toLocaleString('sv-SE')} kr</span>
                  </div>
                </div>
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-medium">Totalt avdrag</span>
                    <span className="font-bold text-green-600">{result.total_deduction?.toLocaleString('sv-SE')} kr</span>
                  </div>
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-medium">Kund betalar</span>
                    <span className="font-bold">{result.customer_pays?.toLocaleString('sv-SE')} kr</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Årssammanfattning {yearSummary?.year}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-muted rounded-lg">
                  <span>ROT-avdrag</span>
                  <span className="font-semibold">{yearSummary?.rot?.total_deduction?.toLocaleString('sv-SE') || 0} kr</span>
                </div>
                <div className="flex justify-between p-3 bg-muted rounded-lg">
                  <span>RUT-avdrag</span>
                  <span className="font-semibold">{yearSummary?.rut?.total_deduction?.toLocaleString('sv-SE') || 0} kr</span>
                </div>
                <div className="flex justify-between p-3 bg-primary/10 rounded-lg font-medium">
                  <span>Totalt</span>
                  <span>{yearSummary?.combined_total?.toLocaleString('sv-SE') || 0} kr</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============ F-SKATT SECTION ============

const FSkattSection = ({ token }) => {
  const [orgNumber, setOrgNumber] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifyingSupplier, setVerifyingSupplier] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(res.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const verifyOrgNumber = async () => {
    if (!orgNumber) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fskatt/verify/${orgNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte verifiera F-skatt");
    } finally {
      setLoading(false);
    }
  };

  const verifySupplier = async (supplierId) => {
    setVerifyingSupplier(supplierId);
    try {
      const res = await axios.post(`${API}/fskatt/verify-supplier/${supplierId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`F-skatt verifierad för ${res.data.supplier_name}`);
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte verifiera");
    } finally {
      setVerifyingSupplier(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Kontrollera F-skatt
          </CardTitle>
          <CardDescription>
            Verifiera att leverantörer har F-skattsedel innan betalning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <Input 
              value={orgNumber}
              onChange={(e) => setOrgNumber(e.target.value)}
              placeholder="Organisationsnummer (t.ex. 5560747569)"
              className="max-w-xs"
            />
            <Button onClick={verifyOrgNumber} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Verifiera
            </Button>
          </div>

          {verificationResult && (
            <Card className={verificationResult.has_fskatt ? "bg-green-50 dark:bg-green-950/20 border-green-200" : 
                            verificationResult.has_fskatt === false ? "bg-red-50 dark:bg-red-950/20 border-red-200" :
                            "bg-amber-50 dark:bg-amber-950/20 border-amber-200"}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  {verificationResult.has_fskatt ? (
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  ) : verificationResult.has_fskatt === false ? (
                    <XCircle className="h-8 w-8 text-red-500" />
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium text-lg">
                      {verificationResult.has_fskatt ? "Godkänd för F-skatt" :
                       verificationResult.has_fskatt === false ? "Saknar F-skatt" :
                       "Kunde inte verifieras"}
                    </p>
                    {verificationResult.company_name && (
                      <p className="text-muted-foreground">{verificationResult.company_name}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      Org.nr: {verificationResult.org_number} | 
                      Verifierad: {new Date(verificationResult.verification_date).toLocaleString('sv-SE')}
                    </p>
                    {verificationResult.error && (
                      <p className="text-sm text-amber-600 mt-1">{verificationResult.error}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Leverantörers F-skattstatus</CardTitle>
          <CardDescription>
            Klicka på en leverantör för att verifiera F-skatt
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left">Leverantör</th>
                  <th className="p-3 text-left">Org.nr</th>
                  <th className="p-3 text-left">F-skatt status</th>
                  <th className="p-3 text-left">Senast verifierad</th>
                  <th className="p-3 text-right">Åtgärd</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map(supplier => (
                  <tr key={supplier.id} className="border-t">
                    <td className="p-3 font-medium">{supplier.name}</td>
                    <td className="p-3 font-mono">{supplier.org_number || '-'}</td>
                    <td className="p-3">
                      {supplier.fskatt_status?.has_fskatt === true ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Godkänd
                        </Badge>
                      ) : supplier.fskatt_status?.has_fskatt === false ? (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Saknas
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Ej verifierad</Badge>
                      )}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {supplier.fskatt_status?.verified_at ? 
                        new Date(supplier.fskatt_status.verified_at).toLocaleDateString('sv-SE') : 
                        '-'}
                    </td>
                    <td className="p-3 text-right">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => verifySupplier(supplier.id)}
                        disabled={!supplier.org_number || verifyingSupplier === supplier.id}
                      >
                        {verifyingSupplier === supplier.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      Inga leverantörer registrerade
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SwedishIntegrations;
